import React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';

/**
 * Module 4 — Filters bar.
 * Compact, horizontal scroll on mobile, wraps on desktop.
 *
 * Filters:
 *   - tier (1-10)
 *   - alert_status (none / at_risk / critical)
 *   - rank_up_eligibility_7d15h (yes / no)
 *   - activeness_level (1-5)
 *   - priority_category (high_upside / high_risk / balanced / low_priority)
 *
 * State managed via the `filters` prop (object) + `onChange(next)` callback.
 */
const FilterChip = ({ active, onClick, color = 'cyan', children }) => (
  <Chip
    color={active ? color : 'cyan'}
    solid={active}
    size="sm"
    onClick={onClick}
    style={{
      cursor: 'pointer',
      opacity: active ? 1 : 0.7,
    }}
  >
    {children}
  </Chip>
);

const CreatorFilters = ({ filters, onChange, isDesktop = false }) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';

  const toggle = (key, value) => {
    onChange?.({ ...filters, [key]: filters?.[key] === value ? null : value });
  };

  const scrollerStyle = {
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start',
    flexWrap: isDesktop ? 'wrap' : 'nowrap',
    overflowX: isDesktop ? 'visible' : 'auto',
    padding: '0 14px 8px',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  };

  const fadeStyle = {
    position: 'absolute',
    top: 0,
    bottom: 8,
    insetInlineEnd: 0,
    width: 32,
    pointerEvents: 'none',
    background: `linear-gradient(to ${isRtl ? 'left' : 'right'}, transparent, ${TCN.bg})`,
  };

  const inner = (
    <>
      <Group label={t('tcn.m4.filters.tier', 'Tier')}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((tier) => (
          <FilterChip
            key={`tier-${tier}`}
            active={filters?.tier === tier}
            onClick={() => toggle('tier', tier)}
          >
            T{tier}
          </FilterChip>
        ))}
      </Group>

      <Group label={t('tcn.m4.filters.alert', 'Alert')}>
        {['none', 'at_risk', 'critical'].map((s) => (
          <FilterChip
            key={`alert-${s}`}
            active={filters?.alert_status === s}
            onClick={() => toggle('alert_status', s)}
            color={s === 'critical' ? 'red' : s === 'at_risk' ? 'amber' : 'green'}
          >
            {t(`tcn.m4.alert.${s}`, s.replace('_', ' '))}
          </FilterChip>
        ))}
      </Group>

      <Group label={t('tcn.m4.filters.rankUp', 'Rank-up')}>
        <FilterChip
          active={filters?.rank_up_eligibility_7d15h === true}
          onClick={() => toggle('rank_up_eligibility_7d15h', true)}
        >
          {t('tcn.m4.rankUpYes', 'Yes')}
        </FilterChip>
        <FilterChip
          active={filters?.rank_up_eligibility_7d15h === false}
          onClick={() => toggle('rank_up_eligibility_7d15h', false)}
        >
          {t('tcn.m4.rankUpNo', 'No')}
        </FilterChip>
      </Group>

      <Group label={t('tcn.m4.filters.activeness', 'Activeness')}>
        {[1, 2, 3, 4, 5].map((lvl) => (
          <FilterChip
            key={`lvl-${lvl}`}
            active={filters?.activeness_level === lvl}
            onClick={() => toggle('activeness_level', lvl)}
          >
            L{lvl}
          </FilterChip>
        ))}
      </Group>

      <Group label={t('tcn.m4.filters.priority', 'Priority')}>
        {['high_upside', 'high_risk', 'balanced', 'low_priority'].map((p) => (
          <FilterChip
            key={`pri-${p}`}
            active={filters?.priority_category === p}
            onClick={() => toggle('priority_category', p)}
            color={p === 'high_risk' ? 'red' : p === 'high_upside' ? 'green' : 'cyan'}
          >
            {t(`tcn.m4.priority.${p}`, p)}
          </FilterChip>
        ))}
      </Group>
    </>
  );

  if (isDesktop) {
    return <div style={scrollerStyle}>{inner}</div>;
  }

  return (
    <div style={{ position: 'relative' }}>
      <div style={scrollerStyle}>{inner}</div>
      <div style={fadeStyle} aria-hidden="true" />
    </div>
  );
};

const Group = ({ label, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: TCN.textMute,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
    <div style={{ display: 'flex', gap: 4 }}>{children}</div>
  </div>
);

export default CreatorFilters;
